/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React, { ReactNode } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import SearchUtils from '../../../utils/SearchUtils';
import Carousel from '@cchome/carousel';
import '../SearchDropDownQuickActions/SearchDropDownQuickActions.scss';
import HorizontalRuler from '../../HorizontalRuler';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchDropDownSummaryItem from './SearchDropDownLeoOnboardingItem';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import { SEARCH_GEN_AI_ICON } from '../../../containers/SearchResultsPage/SearchConstants';
import './SearchDropdownLeoOnboarding.scss';

type OwnProps = {
	summaryData: any;
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	searchQuery: string;
	hostShowSearchDropdown: boolean;
};

interface State {
	areChildrenFocusable: boolean;
}

type SearchDropDownGenSummaryProps = OwnProps & WrappedComponentProps & PropsFromRedux;

class SearchDropdownOnboardingSummary extends ClickBase<SearchDropDownGenSummaryProps, State> {
	analytics: SearchAnalytics;
	genAISummaryRef = React.createRef<HTMLDivElement>();
	constructor(props) {
		super(props);
		this.state = {
			areChildrenFocusable: false,
		};
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.onCarouselClick = this.onCarouselClick.bind(this);
	}

	componentDidUpdate(prevProps: Readonly<SearchDropDownGenSummaryProps>, prevState: Readonly<State>): void {
		if (this.state.areChildrenFocusable !== prevState.areChildrenFocusable) {
			if (this.state.areChildrenFocusable) {
				const firstFocusable = document.getElementById('search-dropdown-gen-ai-summary-item-0');
				if (firstFocusable) {
					firstFocusable.focus();
				}
			} else {
				this.genAISummaryRef.current.focus();
			}
		}
	}

	renderGenTitle = () => {
		const headerTextSummaryId = 'app.search.presearch.genai.title';
		return (
			<div className='search-dropdown-gen-ai-summary-title-container'>
				<img className='gen-ai-summary-logo' alt='' src={SEARCH_GEN_AI_ICON} />
				<span className='search-dropdown-gen-ai-summary-title search-dropdown-summary-title'>
					<h5 role='presentation'>
						<FormattedMessage id={headerTextSummaryId} />
					</h5>
				</span>
			</div>
		);
	};

	onCarouselClick = (e: any) => {
		this.analytics.postPreSearchCarouselScroll(e, 'RecommendedSummaries');
	};

	makeChildrenFocusable = () => {
		this.setState({ areChildrenFocusable: true });
	};

	makeChildrenBlurred = () => {
		this.setState({ areChildrenFocusable: false });
	};

	keyDownHandler = (event: Record<string, any>) => {
		const key = event.key;
		const keyCode = event.keyCode;
		if (key === 'Enter' || keyCode === 13) {
			this.makeChildrenFocusable();
		} else if (key === 'Escape' || keyCode === 27) {
			if (this.state.areChildrenFocusable) {
				event.stopPropagation();
				event.preventDefault();
				this.makeChildrenBlurred();
			}
		}
	};

	renderQuickActions = () => {
		const headerTextSummaryId = 'app.search.presearch.genai.title';
		const { summaryData, searchHostContext, logger, onSubmit, searchQuery, hostShowSearchDropdown } = this.props;
		if (!Array.isArray(summaryData) || !summaryData.length) {
			return null;
		}

		this.analytics.postPreSearchItemAction({
			eventType: 'render',
			recommendationType: 'Leo Recommended Summaries',
			uiSortOrder: 0, // todo : discuss order
			data: this.props.summaryData,
		});

		const quickSummaryItems = summaryData.map((summaryItem, index) => (
			<SearchDropDownSummaryItem
				key={index}
				data={summaryItem}
				index={index}
				count={summaryData.length}
				lastCard={index === summaryData.length - 1}
				searchHostContext={searchHostContext}
				logger={logger}
				onSubmit={onSubmit}
				searchQuery={searchQuery}
				hostShowSearchDropdown={hostShowSearchDropdown}
				tabIndex={this.state.areChildrenFocusable}
			/>
		));

		return (
			<>
				<div className='search-dropdown-gen-ai-summary-container'>
					<div className='search-dropdown-gen-ai-summary-header text-container'>{this.renderGenTitle()}</div>
					<div id='search-dropdown-gen-ai-summary-desc' className='sr-only'>
						{`${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.enter',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.carousel',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.exit',
						})}`}
					</div>
					<div
						className='search-dropdown-gen-ai-summary'
						id='search-dropdown-gen-ai-summary'
						role='option'
						tabIndex={this.state.areChildrenFocusable ? undefined : -1}
						onKeyDown={this.keyDownHandler}
						ref={this.genAISummaryRef}
						aria-describedby='search-dropdown-gen-ai-summary-desc'
						aria-label={this.props.intl.formatMessage({
							id: headerTextSummaryId,
						})}
					>
						<Carousel
							isCCX={SearchUtils.applyBorderBox()}
							darkMode={SearchUtils.isDarkMode(this.props.theme)}
							actionEvents={this.onCarouselClick}
							carouselBtnLabels={{
								previous: 'suggestions',
								next: 'suggestions',
							}}
							keepWidth
						>
							{quickSummaryItems}
						</Carousel>
					</div>
				</div>
				<HorizontalRuler />
			</>
		);
	};

	render(): ReactNode | null {
		return this.renderQuickActions();
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
	presearchResultsPayload: search.presearchResultsPayload,
	searchAppMetadataList: search.searchAppMetadataList,
});
const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownOnboardingSummary));
